'use client'

// Ловит ошибки страницы на устройстве ученика и отправляет в /api/client-errors
// (решение владельца 12.10.2026). Что уходит: вид, текст ошибки, первые строки
// стека, путь экрана без query, сокращённый user agent и номер сборки. Тексты
// ученика, адрес почты и содержимое полей не трогаем. Каждая ошибка уходит
// один раз за загрузку страницы, не больше 10 за загрузку.
import { useEffect } from 'react'
import { BUILD_ID } from '@/lib/app-version'

const MAX_PER_PAGE = 10
const STACK_LINES = 8

function device(): string {
  const ua = navigator.userAgent
  const m = ua.match(/\(([^)]*)\)/)
  const platform = m ? m[1].split(';').slice(0, 3).join(';').trim() : ''
  const browser = ua.match(/(Chrome|CriOS|Firefox|FxiOS|Safari|YaBrowser|SamsungBrowser|Edg|OPR)\/[\d.]+/g)?.slice(-1)[0] ?? ''
  return `${browser} ${platform} ${window.innerWidth}x${window.innerHeight}`.trim()
}

export function ClientErrorWatch() {
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && process.env.NEXT_PUBLIC_CLIENT_ERRORS !== '1') return
    const sent = new Set<string>()
    let n = 0

    function send(kind: 'error' | 'rejection' | 'resource', message: string, stack?: string | null) {
      const key = `${kind}|${message.slice(0, 200)}`
      if (sent.has(key) || n >= MAX_PER_PAGE) return
      sent.add(key)
      n += 1
      const body = JSON.stringify({
        kind,
        message: message.slice(0, 1000),
        stack: stack ? stack.split('\n').slice(0, STACK_LINES).join('\n').slice(0, 4000) : null,
        page: window.location.pathname,
        device: device(),
        build: BUILD_ID || null,
      })
      try {
        if (navigator.sendBeacon && navigator.sendBeacon('/api/client-errors', new Blob([body], { type: 'application/json' }))) return
      } catch {
        // падаем на fetch ниже
      }
      fetch('/api/client-errors', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {})
    }

    function onError(e: ErrorEvent) {
      // Не загрузился скрипт, стиль или картинка: у события нет message, есть target.
      if (!e.message && e.target && e.target !== window) {
        const t = e.target as HTMLElement & { src?: string; href?: string }
        // У файла есть запасной адрес, загрузку повторят оттуда (yandex-metrika.tsx).
        if (t.dataset?.errFallback === '1') return
        const url = t.src || t.href
        if (url && /\.(js|css|mjs)(\?|$)/.test(url)) send('resource', `Не загрузился ${t.tagName.toLowerCase()}: ${url.replace(/^https?:\/\/[^/]+/, '')}`)
        return
      }
      const err = e.error as { message?: string; stack?: string } | undefined
      send('error', e.message || err?.message || 'Ошибка без текста', err?.stack ?? null)
    }
    function onRejection(e: PromiseRejectionEvent) {
      const r = e.reason as { message?: string; stack?: string } | string | undefined
      const message = typeof r === 'string' ? r : r?.message || 'Отклонённый промис без текста'
      send('rejection', message, typeof r === 'object' && r ? (r.stack ?? null) : null)
    }
    window.addEventListener('error', onError, true)
    window.addEventListener('unhandledrejection', onRejection)
    return () => {
      window.removeEventListener('error', onError, true)
      window.removeEventListener('unhandledrejection', onRejection)
    }
  }, [])
  return null
}
